import type { Cookie, CookieSetOptions } from 'universal-cookie';
import Cookies from 'universal-cookie';

const DEFAULT_COOKIE_OPTIONS: CookieSetOptions = {
  path: '/',
  domain: import.meta.env.NODE_ENV !== 'production' ? '.stag.works' : '.codeginx.com',
  secure: true,
  sameSite: 'lax',
};

const AUTH_COOKIE_OPTIONS: CookieSetOptions = {
  ...DEFAULT_COOKIE_OPTIONS,
  // Bearer tokens must remain JS-readable to attach Authorization headers.
  // Keep them out of document-wide defaults for non-auth cookies where possible.
  maxAge: 60 * 60 * 24 * 7,
};

export class CookieManager {
  static COOKIE_PREFIX = '';

  static AUTH_TOKEN = 'auth_token';

  static TOKEN_EXPIRE_DAYS = 7;

  static MENU_STATE = 'menu';

  static AGREEMENT_DELIVERABLES_STATE = 'agreement_deliverables_state';

  static cookies = new Cookies();

  static getPrefixedKey(key: string): string {
    return `${this.COOKIE_PREFIX}${key}`;
  }

  static matchKeys(key1: string, key2: string): boolean {
    return key1 === this.getPrefixedKey(key2);
  }

  static get(key: string /* , options: CookieSetOptions = DEFAULT_COOKIE_OPTIONS */) {
    return this.cookies.get(this.getPrefixedKey(key));
  }

  static set(key: string, value: Cookie, options: CookieSetOptions = DEFAULT_COOKIE_OPTIONS): void {
    this.cookies.set(this.getPrefixedKey(key), value, options);
  }

  static remove(key: string, options: CookieSetOptions = DEFAULT_COOKIE_OPTIONS): void {
    this.cookies.remove(this.getPrefixedKey(key), options);
  }

  static getAccessToken(): string | undefined {
    const token = this.get(this.AUTH_TOKEN);
    const localStorageToken = localStorage.getItem('accessToken');

    return typeof token === 'string' && token.length > 0 ? token : localStorageToken || undefined;
  }

  static setAccessToken(token: string, options: CookieSetOptions = AUTH_COOKIE_OPTIONS): void {
    localStorage.setItem('accessToken', token || '');
    this.set(this.AUTH_TOKEN, token, {
      ...AUTH_COOKIE_OPTIONS,
      ...options,
      maxAge: 60 * 60 * 24 * this.TOKEN_EXPIRE_DAYS,
    });
  }

  static removeAuthTokens(options: CookieSetOptions = AUTH_COOKIE_OPTIONS): void {
    this.remove(this.AUTH_TOKEN, options);
    // Clear legacy cookie name from the previous XSRF/session flow.
    this.remove('XSRF-TOKEN', options);
  }

  static isLoggedIn(): boolean {
    return Boolean(this.getAccessToken());
  }

  static setMenuState(status: string) {
    return this.set(this.MENU_STATE, status);
  }

  static getMenuState() {
    return this.get(this.MENU_STATE);
  }

  static setAgreementDeliverablesState(state: string) {
    return this.set(this.AGREEMENT_DELIVERABLES_STATE, state);
  }

  static getAgreementDeliverablesState() {
    return this.get(this.AGREEMENT_DELIVERABLES_STATE);
  }
}
